import axios from 'axios';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API_URL,
  withCredentials: true,
});

// Send the user's chosen UI language on every request so the backend can
// localize emails (verification, password reset, etc.) and other server-side
// outputs to match what the user sees on screen — regardless of OS locale.
api.interceptors.request.use((config) => {
  try {
    const lang = (localStorage.getItem('lang') || 'pl').slice(0, 2);
    config.headers = config.headers || {};
    if (!config.headers['Accept-Language']) {
      config.headers['Accept-Language'] = lang;
    }
  } catch (_) { /* ignore */ }
  return config;
});

const ADMIN_PATH = '/flvbybcnhfnjh-gfytkm-8246';
let isRefreshing = false;
let failedQueue = [];
let refreshAttempts = 0;
const MAX_REFRESH_ATTEMPTS = 2;

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) prom.reject(error);
    else prom.resolve(token);
  });
  failedQueue = [];
};

const clearAdminSession = () => {
  localStorage.removeItem('access_token');
  isRefreshing = false;
  refreshAttempts = 0;
  failedQueue = [];
  if (!window.location.pathname.endsWith('/login')) {
    window.location.href = `${ADMIN_PATH}/login`;
  }
};

const clearUserSession = () => {
  localStorage.removeItem('user_token');
  localStorage.removeItem('user_data');
  isRefreshing = false;
  refreshAttempts = 0;
  failedQueue = [];
};

api.interceptors.request.use((config) => {
  const isAdmin = config.url?.startsWith('/admin') || window.location.pathname.startsWith(ADMIN_PATH);
  const token = isAdmin ? localStorage.getItem('access_token') : localStorage.getItem('user_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

api.interceptors.response.use(
  (response) => {
    refreshAttempts = 0;
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status !== 401 || originalRequest._retry) {
      return Promise.reject(error);
    }

    const isAdmin = originalRequest.url?.startsWith('/admin') || window.location.pathname.startsWith(ADMIN_PATH);

    // Don't retry refresh requests — stop the cycle
    if (originalRequest.url?.includes('/refresh')) {
      if (isAdmin) clearAdminSession();
      else clearUserSession();
      return Promise.reject(error);
    }

    // Safety: max refresh attempts to break any possible loop
    if (refreshAttempts >= MAX_REFRESH_ATTEMPTS) {
      if (isAdmin) clearAdminSession();
      else clearUserSession();
      return Promise.reject(error);
    }

    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        failedQueue.push({ resolve, reject });
      }).then(token => {
        originalRequest.headers.Authorization = `Bearer ${token}`;
        return api(originalRequest);
      }).catch(err => Promise.reject(err));
    }

    originalRequest._retry = true;
    isRefreshing = true;
    refreshAttempts++;

    try {
      if (isAdmin) {
        const res = await api.post('/admin/refresh');
        const token = res.data.access_token;
        localStorage.setItem('access_token', token);
        processQueue(null, token);
        isRefreshing = false;
        originalRequest.headers.Authorization = `Bearer ${token}`;
        return api(originalRequest);
      } else {
        clearUserSession();
        processQueue(new Error('User token expired'));
        return Promise.reject(error);
      }
    } catch (refreshError) {
      processQueue(refreshError);
      if (isAdmin) clearAdminSession();
      else clearUserSession();
      return Promise.reject(refreshError);
    } finally {
      isRefreshing = false;
    }
  }
);

export default api;

export const getImageUrl = (path) => {
  if (!path) return '';
  if (path.startsWith('http')) return path;
  return `${process.env.REACT_APP_BACKEND_URL}${path.startsWith('/') ? '' : '/'}${path}`;
};
